import React, { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTenantStore } from '@/store/tenantStore'
import { useAuthStore } from '@/store/authStore'
import { tenantsApi } from '@/modules/system/tenants/api/tenantsApi'

// Backend returns Role.Name ("System Administrator") or Role.Code ("SYSTEM_ADMIN")
const ADMIN_ROLES = ['Admin', 'SystemAdmin', 'SuperAdmin', 'SYSTEM_ADMIN', 'System Administrator']

const TenantSelector: React.FC = () => {
  const queryClient = useQueryClient()
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const setActiveTenantId = useTenantStore((s) => s.setActiveTenantId)
  const hasRole = useAuthStore((s) => s.hasRole)

  const [active, setActive] = useState(false)
  const [tenants, setTenants] = useState<{ tenantId: number; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isAdmin = ADMIN_ROLES.some((r) => hasRole(r))
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    setLoading(true)
    tenantsApi
      .list({ pageSize: 200 })
      .then((res) => setTenants(res.items.map((t) => ({ tenantId: t.tenantId, name: t.name }))))
      .catch(() => setTenants([]))
      .finally(() => setLoading(false))
  }, [isAuthenticated, isAdmin])

  // Auto-select first tenant when admin has none selected
  useEffect(() => {
    if (!isAdmin || loading || tenants.length === 0) return
    if (activeTenantId == null) {
      setActiveTenantId(tenants[0].tenantId)
      queryClient.invalidateQueries()
    }
  }, [isAdmin, loading, tenants, activeTenantId, setActiveTenantId, queryClient])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActive(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentName = tenants.find((t) => t.tenantId === activeTenantId)?.name ?? (activeTenantId ? `#${activeTenantId}` : 'Tenant seçin')

  const handleSelect = (id: number) => {
    setActiveTenantId(id)
    setActive(false)
    queryClient.invalidateQueries()
  }

  if (!isAdmin) return null

  return (
    <div
      className="relative tenant-selector mx-[8px] md:mx-[10px] ltr:first:ml-0 rtl:first:mr-0"
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={() => setActive((a) => !a)}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-md border transition-all text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#172036] ${
          active ? 'ring-2 ring-primary-500' : 'border-gray-200 dark:border-[#172036]'
        }`}
        title="Tenant seç"
      >
        <i className="material-symbols-outlined !text-[18px] text-gray-500">business</i>
        <span className="max-w-[120px] truncate text-black dark:text-white">{loading ? '…' : currentName}</span>
        <i className="ri-arrow-down-s-line text-[14px] text-gray-500"></i>
      </button>

      {active && (
        <div className="absolute mt-2 w-[220px] max-h-[280px] overflow-y-auto z-[100] bg-white dark:bg-[#0c1427] border border-gray-200 dark:border-[#172036] rounded-md shadow-lg py-1">
          {tenants.length === 0 && !loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Tenant bulunamadı</div>
          ) : (
            tenants.map((t) => (
              <button
                key={t.tenantId}
                type="button"
                onClick={() => handleSelect(t.tenantId)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#172036] ${
                  t.tenantId === activeTenantId ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium' : ''
                }`}
              >
                {t.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default TenantSelector
